import { useState, useCallback, useEffect, useRef } from 'react';
import type { Message, ChatSession } from '@/types';
import {
  getMessages,
  sendMessage as apiSendMessage,
  createSessionWithMessage,
} from '@/lib/chat-api';

interface UseChatOptions {
  sessionId?: string;
}

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  session: ChatSession | null;
  sendMessage: (content: string) => Promise<void>;
  startNewSession: (agentId?: string) => Promise<void>;
  clearError: () => void;
}

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>();
  // Track if session is pending (not yet saved to DB)
  const isPendingSessionRef = useRef(true);

  // Load messages when session changes
  useEffect(() => {
    if (!options.sessionId) return;

    async function loadMessages() {
      try {
        const loadedMessages = await getMessages(options.sessionId!);
        setMessages(loadedMessages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      }
    }

    loadMessages();
  }, [options.sessionId]);

  // Create a pending session (local only, not saved to DB)
  // Session will be persisted when first message is sent
  const startNewSession = useCallback(async (agentId?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Create a local pending session (NOT saved to DB yet)
      const pendingSession: ChatSession = {
        id: crypto.randomUUID(), // Temporary ID
        userId: 'pending',
        title: null,
        category: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setSession(pendingSession);
      setMessages([]);
      setSelectedAgentId(agentId); // Store agent ID for when message is sent
      isPendingSessionRef.current = true; // Mark as pending
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!session) {
        setError('No active session');
        return;
      }

      setIsLoading(true);
      setError(null);

      // Optimistically add user message
      const tempUserMessage: Message = {
        id: crypto.randomUUID(),
        sessionId: session.id,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMessage]);

      try {
        if (isPendingSessionRef.current) {
          // First message: create session in DB and send message atomically
          const { session: realSession, message: assistantMessage } = await createSessionWithMessage(
            content,
            { agentId: selectedAgentId }
          );
          setSession(realSession); // Update with real session from DB
          isPendingSessionRef.current = false; // No longer pending
          // Add assistant response from HTTP
          setMessages((prev) => {
            if (prev.some((m) => m.id === assistantMessage.id)) return prev;
            return [...prev, assistantMessage];
          });
        } else {
          // Existing session: send message and capture assistant response
          const assistantMessage = await apiSendMessage(session.id, content);
          setMessages((prev) => {
            if (prev.some((m) => m.id === assistantMessage.id)) return prev;
            return [...prev, assistantMessage];
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      } finally {
        setIsLoading(false);
      }
    },
    [session, selectedAgentId]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    messages,
    isLoading,
    error,
    session,
    sendMessage,
    startNewSession,
    clearError,
  };
}
